/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import DateField from "@components/FormsV2/DateField/DateField";
import ControlledDropDownV2 from "@components/Forms/ControlledDropDownV2";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useConsumptionReport } from "./api";
import CreditsBarChart from "./CreditsBarChart";
import { defaultStartDate } from "./helpers";
import { BUCKET_SIZES, BucketSize } from "./types";
import { useStyles } from "./styles";

const StatCard: React.FC<{
  label: string;
  value: number;
  badge?: string;
  sublabel?: string;
}> = ({ label, value, badge, sublabel }) => {
  const styles = useStyles();
  return (
    <Card body css={styles.summaryCard}>
      <div css={styles.statLabelRow}>
        <span css={styles.statLabel}>{label}</span>
        {badge && <span css={styles.statBadge}>{badge}</span>}
      </div>
      <div css={styles.statValue}>{value}</div>
      {sublabel && <div css={styles.statSublabel}>{sublabel}</div>}
    </Card>
  );
};

// RTK Query surfaces HTTP failures as a FetchBaseQueryError with a numeric
// `status`. A 502 is the API's "billing provider unavailable" response.
const errorMessage = (error: unknown): string => {
  const status = (error as { status?: number } | undefined)?.status;
  if (status === 502) {
    return "The billing provider is temporarily unavailable, so credit balances could not be retrieved. Please try again shortly.";
  }
  return "Could not load consumption report.";
};

const AccountCredits: React.FC = () => {
  const styles = useStyles();
  const [startDate, setStartDate] = useState<string>(defaultStartDate());
  const [bucketSize, setBucketSize] = useState<BucketSize>("month");
  const { runReport, report, isLoading, isUninitialized, error } =
    useConsumptionReport();
  const showSpinner = isLoading || isUninitialized;
  const notProvisioned = !showSpinner && !error && !report.available;

  useEffect(() => {
    runReport({ startDate, bucketSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, bucketSize]);

  // "Not provisioned" is an account-level state (no ledger at all), not a
  // transient one — replace the whole page rather than showing zeroed stats.
  if (notProvisioned) {
    return (
      <Card css={styles.chartCard}>
        <Card.Body>
          <div css={styles.emptyState}>
            Credit tracking isn&apos;t enabled for this account.
          </div>
        </Card.Body>
      </Card>
    );
  }

  const currentBalanceSublabel = report.inOverdraft
    ? `Provisioned: ${report.provisionedBalance} · Overage used: ${report.overdraft.used}`
    : undefined;

  return (
    <div>
      <Row css={styles.summaryRow}>
        <Col md={3} css={styles.summaryCol}>
          <StatCard label="Starting Balance" value={report.startingBalance} />
        </Col>
        <Col md={3} css={styles.summaryCol}>
          <StatCard
            label="Current Balance"
            value={report.currentBalance}
            badge={report.inOverdraft ? "In Overdraft" : undefined}
            sublabel={currentBalanceSublabel}
          />
        </Col>
        <Col md={3} css={styles.summaryCol}>
          <StatCard label="Total Consumed" value={report.totalConsumed} />
        </Col>
        <Col md={3} css={styles.summaryCol}>
          <Card body css={styles.inputsCard}>
            <div css={styles.inputField}>
              <DateField
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div css={styles.inputField}>
              <ControlledDropDownV2
                label="Bucket Size"
                id="account-credits-bucket-size"
                value={bucketSize}
                onChange={(e) => setBucketSize(e.target.value as BucketSize)}
                required={false}
                readOnly={false}
                disabled={false}
                options={BUCKET_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size.charAt(0).toUpperCase() + size.slice(1)}
                  </option>
                ))}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Card css={styles.chartCard}>
        <Card.Header css={styles.chartCardHeader}>Account Credits</Card.Header>
        <Card.Body>
          {showSpinner && <ScopeStackSpinner />}

          {!showSpinner && Boolean(error) && (
            <div css={styles.errorState}>{errorMessage(error)}</div>
          )}

          {!showSpinner && !error && report.buckets.length === 0 && (
            <div css={styles.emptyState}>No consumption in this period.</div>
          )}

          {!showSpinner && !error && report.buckets.length > 0 && (
            <div css={styles.chartContainer}>
              <CreditsBarChart
                buckets={report.buckets}
                bucketSize={bucketSize}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AccountCredits;
